import _ from 'underscore'

import {
  number,
  percentage,
  cubicFeetPerMinute,
  degreesFahrenheit,
  staticPressure as staticPressureFormatter,
  pressure,
  rpm
} from 'helpers/formatters'

/* eslint-disable max-lines */
const OPERATIONS = {
  'COMPRESSOR_COOLING': 'cooling',
  'COMPRESSOR_COOLING_STAGE_1': 'cooling',
  'COMPRESSOR_COOLING_STAGE_2': 'cooling',
  'COMPRESSOR_HEATING_STAGE_1': 'heating',
  'COMPRESSOR_HEATING_STAGE_2': 'heating',
  'COMPRESSOR_HEATING': 'heating',
  'INDOOR_HEATING': 'heating',
  'INDOOR_HEATING_STAGE_1': 'heating',
  'INDOOR_HEATING_STAGE_2': 'heating',
  'INDOOR_HEATING_STAGE_3': 'heating',
  'INDOOR_FAN_ONLY': 'fan on'
}

const indexAtOrBeforeCursor = (timeAtCursor, pointsArray) => {
  return _.reduce(pointsArray, (memo, point, index) => point.occurredAt <= timeAtCursor ? index : memo, null)
}

const dataToPoint = (point, key) => {
  if (point) {
    return {
      x: point.occurredAt,
      y: point[key]
    }
  }
}

const interpolatePoints = (timeAtCursor, pair) => {
  if (!pair) { return null }
  if (!pair[0]?.y) { return pair[1] }
  if (!pair[0]?.y) { return pair[1] }

  const tempDelta = pair[1].y - pair[0].y
  const timeDelta = (timeAtCursor - pair[0].x)
  const timeSpan = (pair[1].x - pair[0].x)

  const scaledTemp = pair[0].y + (tempDelta * (timeDelta / timeSpan))

  return _({}).extend(pair[1], { y: scaledTemp })
}

const findYValueFor = (dataSet, key, timeAtCursor, lineType, breakLineAtNullValues = false) => {
  if (!dataSet || dataSet.length === 0) { return }

  const index = indexAtOrBeforeCursor(timeAtCursor, dataSet)
  if (index == null) { return }

  const pointBefore = dataToPoint(dataSet[index], key)

  if (breakLineAtNullValues && pointBefore === null) { return }

  if (lineType === 'sloped') {
    if (pointBefore && pointBefore.x === timeAtCursor) {
      return pointBefore.y
    }

    const pointAfter = dataToPoint(dataSet[index + 1], key)
    if (!pointAfter) { return }

    const interpolatedPoints = interpolatePoints(timeAtCursor, [pointBefore, pointAfter])
    return interpolatedPoints && interpolatedPoints.y
  } else if (lineType === 'stepped') {
    return pointBefore && pointBefore.y
  } else {
    console.error(`Unexpected value "${lineType}" for lineType.`)
  }
}

const findInterpolatedValueAtCursor = (timeAtCursor, occurrences, key) => {
  const indexOfPointAtCursor = _.findIndex(occurrences, ({ occurredAt }) => occurredAt >= timeAtCursor)
  const pair = [dataToPoint(occurrences[indexOfPointAtCursor - 1], key), dataToPoint(occurrences[indexOfPointAtCursor], key)]

  return interpolatePoints(timeAtCursor, pair)
}

const occurredDuringOperation = (occurredAt, operations) => {
  const previousOperationOccurrence = _.chain(operations).filter(operation => operation.occurredAt <= occurredAt).last().value()
  const nextOperationOccurrence = _.chain(operations).filter(operation => operation.occurredAt >= occurredAt).first().value()

  const occurredOnOperation = _.find(operations, operation => occurredAt === operation.occurredAt)
  const previousOperation = previousOperationOccurrence && previousOperationOccurrence.status ? 'started' : 'stopped'
  const nextOperation = nextOperationOccurrence && nextOperationOccurrence.status ? 'started' : 'stopped'

  const startedPriorDayAndStillActive = !previousOperation && nextOperation === 'stopped'
  const startedAndContinuedIntoNextDay = previousOperation === 'started' && !nextOperation
  const betweenOperations = previousOperation === 'started' && nextOperation === 'stopped'

  return occurredOnOperation || startedPriorDayAndStillActive || startedAndContinuedIntoNextDay || betweenOperations
}

const isRelieving = (zone, timeAtCursor) => {
  return occurredDuringOperation(timeAtCursor, (zone && zone.relievingOccurrences))
}

const yAxisCapacityValue = (chartData, timeAtCursor, location) => {
  const capacityPoints = chartData[location]
  if (capacityPoints) {
    if (capacityPoints.length === 0) { return }

    const index = indexAtOrBeforeCursor(timeAtCursor, capacityPoints)

    return (index || index === 0) && capacityPoints[index].capacity
  }
}

const yAxisSpeedValue = (chartData, timeAtCursor, location) => {
  const speedPoints = chartData[location]
  if (speedPoints) {
    if (speedPoints.length === 0) { return }

    const index = indexAtOrBeforeCursor(timeAtCursor, speedPoints)

    return (index || index === 0) && speedPoints[index].speed
  }
}

const isIntegratedDehumidification = (idsData, timeAtCursor) => {
  if (idsData) {
    const index = indexAtOrBeforeCursor(timeAtCursor, idsData.runOccurrences)
    return (index >= 0 && idsData.runOccurrences[index]?.operation === 'started')
  }

  return false
}

const activeStateAtCursorTime = (rthData, timeAtCursor) => {
  const activeCycles = _.filter(rthData.stages, cycle => {
    const index = indexAtOrBeforeCursor(timeAtCursor, cycle.runOccurrences)
    const previousOccurrence = cycle.runOccurrences[index]
    const allowedStages = [...Object.keys(OPERATIONS), 'SYSTEM_MODE_OFF', 'DEFROST']

    return (allowedStages.includes(cycle.stage) && index >= 0 && (previousOccurrence?.operation === 'started' || previousOccurrence?.capacity > 0))
  })

  if (_.any(activeCycles, (cycle) => cycle.stage === 'SYSTEM_MODE_OFF')) {
    const activeOperation = _.find(activeCycles, (cycle) => OPERATIONS[cycle.stage])

    if (activeOperation) {
      return OPERATIONS[activeOperation.stage]
    }

    return 'Off'
  } else if (_.any(activeCycles, (cycle) => cycle.stage === 'DEFROST')) {
    return 'Defrosting'
  } else if (isIntegratedDehumidification(rthData.integratedDehumidification, timeAtCursor)) {
    return 'Cooling (IDS)'
  } else if (activeCycles.length) {
    return OPERATIONS[activeCycles[0].stage]
  }
}

const zoneMode = (zone, timeAtCursor) => {
  if (!(zone && zone.modeOccurrences && zone.modeOccurrences.length > 0)) { return }

  const mostRecentModeOccurrence =
    _.chain(zone.modeOccurrences)
      .filter((occurrence) => occurrence.occurredAt <= timeAtCursor)
      .max((occurrence) => occurrence.occurredAt)
      .value()

  if (!mostRecentModeOccurrence) { return }

  if (mostRecentModeOccurrence.mode === 'EMERGENCY_HEAT') {
    return 'E HEAT'
  } else {
    return mostRecentModeOccurrence.mode
  }
}

const buildZones = (timeAtCursor, rthData) => {
  const zones = []

  _.each(rthData.zones, zone => {
    const coolingSetpoint = findYValueFor(zone.coolingSetpointOccurrences ?? [], 'temperature', timeAtCursor, 'stepped', true)
    const heatingSetpoint = findYValueFor(zone.heatingSetpointOccurrences ?? [], 'temperature', timeAtCursor, 'stepped', true)
    const indoorTemp = findInterpolatedValueAtCursor(timeAtCursor, zone.tempOccurrences ?? [], 'temperature')
    const indoorHumidity = findInterpolatedValueAtCursor(timeAtCursor, zone.humidityOccurrences ?? [], 'humidity')
    const relieving = isRelieving(zone, timeAtCursor) // relievingOccurrences
    const damperPosition = findYValueFor(zone.damperPositionOccurrences ?? [], 'damperPosition', timeAtCursor, 'stepped', true)
    const status = activeStateAtCursorTime(rthData, timeAtCursor)
    const airflow = yAxisCapacityValue(rthData, timeAtCursor, 'airflowPercentageOccurrences')
    const mode = zoneMode(zone, timeAtCursor) // mode

    zones.push({
      name: zone.name,
      cooling: degreesFahrenheit(coolingSetpoint, 0),
      heating: degreesFahrenheit(heatingSetpoint, 0),
      indoorTemp: degreesFahrenheit((indoorTemp && indoorTemp.y)),
      indoorHumidity: percentage((indoorHumidity && indoorHumidity.y), 1),
      relieving: relieving ? 'Yes' : 'No',
      damperPosition: percentage(damperPosition),
      status,
      airflow: percentage(airflow),
      mode
    })
  })

  return zones
}

export const GetLegendTooltipData = (timeAtCursor, rthData) => {
  const zones = buildZones(timeAtCursor, rthData)
  const indoorCapacity = yAxisCapacityValue(rthData, timeAtCursor, 'furnaceCapacityOccurrences')
  const actualAirflow = findYValueFor(rthData.indoorAirflowActualOccurrences, 'airflow', timeAtCursor, 'stepped')
  const targetAirflow = findYValueFor(rthData.indoorAirflowTargetOccurrences, 'airflow', timeAtCursor, 'stepped')
  const airflowPercentage = yAxisCapacityValue(rthData, timeAtCursor, 'airflowPercentageOccurrences')

  const gasSuperheat = findYValueFor(rthData.indoorGasSuperheatOccurrences, 'temperature', timeAtCursor, 'sloped', true)
  const gasTemp = findYValueFor(rthData.indoorGasTempOccurrences, 'temperature', timeAtCursor, 'sloped')
  const coilTemp = findYValueFor(rthData.indoorCoilTempOccurrences, 'temperature', timeAtCursor, 'sloped')
  const staticPressure = findYValueFor(rthData.indoorMotorStaticPressureOccurrences, 'pressure', timeAtCursor, 'stepped')
  const returnStaticPressure = findYValueFor(rthData.indoorReturnStaticPressureOccurrences, 'pressure', timeAtCursor, 'stepped')
  const supplyStaticPressure = findYValueFor(rthData.indoorSupplyStaticPressureOccurrences, 'pressure', timeAtCursor, 'stepped')
  const outdoorTemp = findYValueFor(rthData.outdoorTemps, 'temperature', timeAtCursor, 'sloped')
  const outdoorHumidity = findYValueFor(rthData.outdoorHumidity, 'humidity', timeAtCursor, 'sloped')
  const outdoorCapacity = yAxisCapacityValue(rthData, timeAtCursor, 'outdoorCompressorDeliveredSpeedOccurrences')
  const odSuctionPressure = findYValueFor(rthData.outdoorSuctionPressureOccurrences, 'pressure', timeAtCursor, 'sloped', true)
  const odSuperheat = findYValueFor(rthData.outdoorSuperheatOccurrences, 'temperature', timeAtCursor, 'sloped', true)
  const odLiquidTemp = findYValueFor(rthData.outdoorLiquidTempOccurrences, 'temperature', timeAtCursor, 'sloped', true)
  const liquidPressure = findYValueFor(rthData.outdoorLiquidPressureOccurrences, 'pressure', timeAtCursor, 'sloped', true)
  const odCompressorActualSpeed = yAxisCapacityValue(rthData, timeAtCursor, 'outdoorCompressorActualSpeedOccurrences')
  const suctionTemperature = findYValueFor(rthData.outdoorSuctionTempOccurrences, 'temperature', timeAtCursor, 'sloped')
  const odSubcool = findYValueFor(rthData.outdoorSubcoolOccurrences, 'temperature', timeAtCursor, 'sloped', true)
  const odGasTemp = findYValueFor(rthData.outdoorGasTempOccurrences, 'temperature', timeAtCursor, 'sloped')
  const odGasPressure = findYValueFor(rthData.outdoorGasPressureOccurrences, 'pressure', timeAtCursor, 'sloped', true)
  const idReturnAirTemp = findYValueFor(rthData.indoorReturnAirTempOccurrences, 'temperature', timeAtCursor, 'sloped')
  const idSupplyAirTemp = findYValueFor(rthData.indoorSupplyAirTempOccurrences, 'temperature', timeAtCursor, 'sloped')
  const idActualSpeedInducer = yAxisSpeedValue(rthData, timeAtCursor, 'indoorInducerSpeedActualOccurrences')
  const idCombustionPressureActual = findYValueFor(rthData.indoorCombustionPressureActualOccurrences, 'pressure', timeAtCursor, 'stepped')
  const idCombustionPressureTarget = findYValueFor(rthData.indoorCombustionPressureTargetOccurrences, 'pressure', timeAtCursor, 'stepped')
  const idGasValvePositionActual = findYValueFor(rthData.indoorGasValvePositionActualOccurrences, 'value', timeAtCursor, 'stepped')
  const idGasValvePositionSetpoint = findYValueFor(rthData.indoorGasValvePositionSetpointOccurrences, 'value', timeAtCursor, 'stepped')
  const idTemperatureRise = findYValueFor(rthData.indoorTemperatureRiseOccurrences, 'temperature', timeAtCursor, 'sloped')
  const idTemperatureRiseTarget = findYValueFor(rthData.indoorTemperatureRiseTargetOccurrences, 'temperature', timeAtCursor, 'sloped')
  const idLineVoltage = findYValueFor(rthData.indoorLineVoltageOccurrences, 'voltage', timeAtCursor, 'stepped', true)
  const requestedModulation = yAxisCapacityValue(rthData, timeAtCursor, 'requestedModulationCapacityOccurrences')
  const idEevStep = findYValueFor(rthData.indoorEevStepPositionOccurrences, 'value', timeAtCursor, 'stepped', true)
  const odInputCurrent = findYValueFor(rthData.outdoorInputCurrentOccurrences, 'current', timeAtCursor, 'stepped', true)
  const odCompressorDischargeTemperature = findYValueFor(rthData.outdoorCompressorDischargeTempOccurrences, 'temperature', timeAtCursor, 'stepped', true)
  const odCompressorPower = findYValueFor(rthData.outdoorCompressorPowerOccurrences, 'power', timeAtCursor, 'stepped', true)
  const odEevStepPosition = findYValueFor(rthData.outdoorEevStepPositionOccurrences, 'value', timeAtCursor, 'stepped', true)
  const odFanPower = findYValueFor(rthData.outdoorFanPowerOccurrences, 'power', timeAtCursor, 'stepped', true)
  const odFanSpeed = findYValueFor(rthData.outdoorFanSpeedOccurrences, 'speed', timeAtCursor, 'stepped', true)
  const odLineVoltage = findYValueFor(rthData.outdoorLineVoltageOccurrences, 'voltage', timeAtCursor, 'stepped', true)
  const odDriveType = findYValueFor(rthData.outdoorDriveTypeOccurrences, 'driveType', timeAtCursor, 'stepped', true)
  const odCoilTemp = findYValueFor(rthData.outdoorCoilTemperatureOccurrences, 'temperature', timeAtCursor, 'sloped', true)

  return {
    zones,
    indoorCapacity: percentage(indoorCapacity),
    actualAirflow: cubicFeetPerMinute(actualAirflow),
    airflowPercentage: percentage(airflowPercentage),
    gasSuperheat: degreesFahrenheit(gasSuperheat),
    gasTemp: degreesFahrenheit(gasTemp, 0),
    coilTemp: degreesFahrenheit(coilTemp, 0),
    staticPressure: staticPressureFormatter(actualAirflow === 0 ? 0 : staticPressure),
    returnStaticPressure: staticPressureFormatter(returnStaticPressure),
    supplyStaticPressure: staticPressureFormatter(supplyStaticPressure),
    targetAirflow: cubicFeetPerMinute(targetAirflow),
    outdoorTemp: degreesFahrenheit(outdoorTemp),
    outdoorHumidity: percentage(outdoorHumidity, 1),
    outdoorCapacity: percentage(outdoorCapacity),
    odSuctionPressure: pressure(odSuctionPressure),
    odSuperheat: degreesFahrenheit(odSuperheat),
    liquidPressure: pressure(liquidPressure),
    suctionTemperature: degreesFahrenheit(suctionTemperature),
    odLiquidTemp: degreesFahrenheit(odLiquidTemp),
    odSubcool: degreesFahrenheit(odSubcool),
    odGasTemp: degreesFahrenheit(odGasTemp),
    odGasPressure: pressure(odGasPressure),
    idReturnAirTemp: degreesFahrenheit(idReturnAirTemp),
    idSupplyAirTemp: degreesFahrenheit(idSupplyAirTemp),
    idEevStep: number(idEevStep),
    idActualSpeedInducer: rpm(idActualSpeedInducer),
    idCombustionPressureActual: staticPressureFormatter(idCombustionPressureActual),
    idCombustionPressureTarget: staticPressureFormatter(idCombustionPressureTarget),
    idGasValvePositionActual: number(idGasValvePositionActual),
    idGasValvePositionSetpoint: number(idGasValvePositionSetpoint),
    idTemperatureRise: degreesFahrenheit(idTemperatureRise),
    idTemperatureRiseTarget: degreesFahrenheit(idTemperatureRiseTarget),
    idLineVoltage: number(idLineVoltage, 1),
    requestedModulation: percentage(requestedModulation, 1),
    odCurrent: number(odInputCurrent),
    odCompressorDischargeTemperature: number(odCompressorDischargeTemperature),
    odCompressorPower: number(odCompressorPower),
    odEevStepPosition: number(odEevStepPosition),
    odFanPower: number(odFanPower),
    odFanSpeed: number(odFanSpeed),
    odLineVoltage: number(odLineVoltage),
    odDriveType,
    odCompressorActualSpeed,
    odCoilTemp: degreesFahrenheit(odCoilTemp)
  }
}

export const ConvertPressureValue = (value) => {
  const match = value.match(/^([\d.]+)\s*(.*)$/)
  return match
    ? `${parseFloat(match[1]) / 100} ${match[2]}`
    : value
}
